// FileViewer.tsx — V6 flat file list (docs/design/design_handoff_files_v6).
import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { format } from "date-fns";
import { Alert, Box as MuiBox, Typography as MuiTypography, CircularProgress, Button as MuiButton } from "@mui/material";
import { File as FileIcon, SlidersHorizontal, Trash2 } from "lucide-react";

import { useAuth } from "@clerk/clerk-react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { RootState } from "@/state/store";
import { fetchUserVectorFiles, removeVectorFile, setDeletingFileId, clearDeletingFileId } from "@/state/vectorFiles/vectorFilesSlice";
import DeleteFileModal from "@/components/FileHistory/components/DeleteFileModal";
import useFileUpload from "@/hooks/useFileUpload";
import { VectorFile } from "@/api/models/vector_file";
import FileScopeModal, { SavedScopeData } from "./FileScopeModal";
import { markWorkProductsNeedingRebuild } from "@/api/api";
import { FileAssignments, queryFileAssignments } from "@/api/fileAssignments";
import { T, FileTypeTile, AssignmentChips, fileKindFromName, fileKindLabel, formatFileSize } from "./filesV6";

// Helper to trigger rebuild for all scopes associated with a file
const triggerRebuildForFileAssignments = async (
    assignment: FileAssignments | undefined,
    token: string
) => {
    if (!assignment || assignment.products.mode === "all") {
        console.log("File is assigned to all products, triggering global rebuild check");
        await markWorkProductsNeedingRebuild(null, null, null, null, true, token);
        return;
    }

    const projectIds = assignment.products.ids;
    if (projectIds.length === 0) return;
    console.log("Triggering rebuild for products:", projectIds);
    await markWorkProductsNeedingRebuild(
        projectIds,
        null,
        null, // user_story_ids
        null, // source_warehouseid
        true, // file_update
        token
    );
};

export interface FileViewerRef {
    refresh: () => void;
    getFileByName: (filename: string) => VectorFile | undefined;
}

const metaDate = (d?: string | Date): string => {
    try {
        const date = typeof d === "string" ? new Date(d) : d ? d : new Date();
        return format(date, "MMM d, yyyy");
    } catch {
        return "";
    }
};

const FileViewer = forwardRef<FileViewerRef, {}>((_, ref) => {
    const dispatch = useAppDispatch();
    const { getToken } = useAuth();
    const status = useAppSelector((s: RootState) => s.vectorFile.vector_file_status) || "idle";
    const files = useAppSelector((s: RootState) => s.vectorFile.vector_file) || [];

    const [selectedFile, setSelectedFile] = useState<VectorFile | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [fileAssignments, setFileAssignments] = useState<Record<number, FileAssignments>>({});
    const [assignmentsLoading, setAssignmentsLoading] = useState(false);
    const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
    const [refreshWarning, setRefreshWarning] = useState<string | null>(null);
    const [showEditScopeModal, setShowEditScopeModal] = useState(false);
    const [editingFile, setEditingFile] = useState<VectorFile | null>(null);

    const globalCount = useMemo(
        () => files.filter(file => fileAssignments[file.fileid]?.products.mode === "all").length,
        [files, fileAssignments]
    );

    const { deleteFile } = useFileUpload({});

    const fetchFiles = useCallback(async () => {
        try {
            await dispatch(fetchUserVectorFiles());
        } catch (e) {
            console.error("Error fetching files:", e);
        }
    }, [dispatch]);

    const fetchFileAssignments = useCallback(async () => {
        if (!files || files.length === 0) return;

        setAssignmentsLoading(true);
        setAssignmentsError(null);
        try {
            const token = await getToken();
            if (!token) {
                console.error("No auth token available");
                return;
            }

            setFileAssignments(await queryFileAssignments(token, files.map(file => file.fileid)));
        } catch (e) {
            console.error("Error fetching file assignments:", e);
            setAssignmentsError(e instanceof Error ? e.message : "File assignments could not be loaded.");
        } finally {
            setAssignmentsLoading(false);
        }
    }, [files, getToken]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    useEffect(() => {
        if (files.length > 0) {
            fetchFileAssignments();
        }
    }, [files, fetchFileAssignments]);

    useImperativeHandle(
        ref,
        () => ({
            refresh: () => {
                void fetchFiles();
                void fetchFileAssignments();
            },
            getFileByName: (filename: string) => {
                return files.find(f => f.filename === filename);
            }
        }),
        [fetchFileAssignments, fetchFiles, files]
    );

    const confirmDelete = useCallback(async () => {
        if (!selectedFile || isDeleting) return;
        setIsDeleting(true);

        const fileId = selectedFile.fileid;

        // Get the file's current scope before deleting (for triggering rebuilds)
        const fileAssignment = fileAssignments[fileId];

        // Set the deleting file ID before starting - this ensures any fetches filter it out
        dispatch(setDeletingFileId(fileId));

        try {
            const result = await deleteFile(fileId);
            if (result.success) {
                // Immediately remove the file from Redux state and close the modal
                dispatch(removeVectorFile(fileId));
                setShowDeleteModal(false);
                setSelectedFile(null);

                // Clear the deleting ID after a short delay to allow any in-flight fetches to complete
                setTimeout(() => {
                    dispatch(clearDeletingFileId());
                }, 1000);

                // Trigger rebuild for all projects/features in the background (don't await)
                const token = await getToken();
                if (token && fileAssignment) {
                    console.log("File deleted, triggering rebuild for affected assignments:", fileAssignment);
                    // Fire and forget - don't block UI on rebuild triggers
                    triggerRebuildForFileAssignments(fileAssignment, token).catch(err => {
                        console.error("Error triggering rebuilds after file deletion:", err);
                    });
                }
            } else {
                console.error("Failed to delete file:", result.error);
                alert(`Failed to delete file: ${result.error}`);
                dispatch(clearDeletingFileId());
            }
        } catch (err) {
            console.error("Error deleting file:", err);
            alert("An unexpected error occurred while deleting the file");
            dispatch(clearDeletingFileId());
        } finally {
            setIsDeleting(false);
        }
    }, [deleteFile, dispatch, isDeleting, selectedFile, fileAssignments, getToken]);

    const cancelDelete = useCallback(() => {
        setShowDeleteModal(false);
        setSelectedFile(null);
    }, []);

    const renderFileRow = (vf: VectorFile) => {
        const kind = fileKindFromName(vf.filename);
        return (
            <MuiBox
                key={vf.fileid}
                sx={{
                    display: "flex", alignItems: "center", gap: "13px", p: "11px 14px",
                    borderRadius: "11px", backgroundColor: T.bgPaper, border: `1px solid ${T.divider}`
                }}
            >
                <FileTypeTile filename={vf.filename} size={34} />
                <MuiBox sx={{ flex: "0 0 340px", minWidth: 0 }}>
                    <MuiTypography title={vf.filename} sx={{ fontSize: 13, fontWeight: 600, color: T.fg1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {vf.filename}
                    </MuiTypography>
                    <MuiTypography sx={{ fontSize: 11, color: T.fg4, fontFamily: T.mono, mt: "2px" }}>
                        {fileKindLabel(kind)} · {formatFileSize(vf.filesize || 0)} · {metaDate(vf.lastupdated || vf.createdate)}
                    </MuiTypography>
                </MuiBox>
                <MuiBox sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                    {assignmentsLoading ? <CircularProgress size={14} /> : <AssignmentChips assignment={fileAssignments[vf.fileid]} />}
                </MuiBox>
                <MuiBox
                    component="button"
                    title="Change assignments"
                    aria-label={`assign-${vf.fileid}`}
                    disabled={assignmentsLoading || Boolean(assignmentsError)}
                    onClick={() => {
                        setEditingFile(vf);
                        setShowEditScopeModal(true);
                    }}
                    sx={{
                        height: 30, px: "12px", borderRadius: "8px", border: `1px solid ${T.divider}`,
                        backgroundColor: T.bgPaper, color: T.fg2,
                        cursor: assignmentsLoading || assignmentsError ? "default" : "pointer",
                        opacity: assignmentsLoading || assignmentsError ? 0.55 : 1,
                        display: "inline-flex", alignItems: "center", gap: "6px", flex: "0 0 auto",
                        fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                        "&:hover": { backgroundColor: T.bgPanel }
                    }}
                >
                    <SlidersHorizontal size={13} />
                    Assign
                </MuiBox>
                <MuiBox
                    component="button"
                    title="Delete file"
                    aria-label={`delete-${vf.fileid}`}
                    onClick={() => {
                        setSelectedFile(vf);
                        setShowDeleteModal(true);
                    }}
                    sx={{
                        width: 30, height: 30, borderRadius: "8px", border: `1px solid ${T.divider}`,
                        backgroundColor: T.bgPaper, color: T.danger, cursor: "pointer", p: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto",
                        "&:hover": { backgroundColor: "rgba(220,38,38,0.06)" }
                    }}
                >
                    <Trash2 size={14} />
                </MuiBox>
            </MuiBox>
        );
    };

    // Loading / error / empty
    if (status === "loading") {
        return (
            <MuiBox sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <MuiTypography sx={{ fontSize: 13, color: T.fg2 }}>Loading files…</MuiTypography>
            </MuiBox>
        );
    }

    if (status === "failed") {
        return (
            <MuiBox sx={{ textAlign: "center", py: 4 }}>
                <MuiTypography sx={{ fontSize: 14, fontWeight: 600, color: T.danger }}>Files couldn’t be loaded</MuiTypography>
                <MuiTypography sx={{ fontSize: 12.5, color: T.fg4, mt: 0.5, mb: 1.5 }}>Check your connection, then try loading the list again.</MuiTypography>
                <MuiButton variant="outlined" size="small" onClick={fetchFiles} sx={{ textTransform: "none" }}>Try again</MuiButton>
            </MuiBox>
        );
    }

    return (
        <>
            {assignmentsError && (
                <Alert
                    severity="error"
                    action={<MuiButton color="inherit" size="small" onClick={fetchFileAssignments}>Retry</MuiButton>}
                    sx={{ mb: 1.5 }}
                >
                    {assignmentsError}
                </Alert>
            )}
            {files.length === 0 ? (
                <MuiBox sx={{ p: 4, textAlign: "center", border: `1px solid ${T.divider}`, borderRadius: "11px", backgroundColor: T.bgPaper }}>
                    <FileIcon size={44} color={T.fg4} style={{ marginBottom: 12 }} />
                    <MuiTypography sx={{ fontSize: 15, fontWeight: 600, color: T.fg2, mb: 0.5 }}>No files uploaded yet</MuiTypography>
                    <MuiTypography sx={{ fontSize: 12.5, color: T.fg4 }}>Upload a file to get started</MuiTypography>
                </MuiBox>
            ) : (
                <MuiBox sx={{ height: "100%", overflowY: "auto", minHeight: 0 }}>
                    {/* List header row */}
                    <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px", m: "0 2px 10px" }}>
                        <MuiTypography sx={{ fontSize: 13, fontWeight: 700, color: T.fg1 }}>All files</MuiTypography>
                        <MuiBox component="span" sx={{ fontFamily: T.mono, fontSize: 11, color: T.fg4, px: "7px", py: "1px", borderRadius: 999, backgroundColor: T.bgInput }}>
                            {files.length}
                        </MuiBox>
                        <MuiBox sx={{ flex: 1 }} />
                        <MuiTypography sx={{ fontSize: 11.5, color: T.fg4 }}>
                            {assignmentsLoading ? "…" : `${globalCount} shared with all products`}
                        </MuiTypography>
                    </MuiBox>
                    {/* File rows */}
                    <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>{files.map(renderFileRow)}</MuiBox>
                </MuiBox>
            )}

            {/* Delete confirmation — UNCHANGED */}
            <DeleteFileModal
                isOpen={showDeleteModal}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                fileName={selectedFile?.filename}
                isDeleting={isDeleting}
            />

            {/* Assign modal — keep the existing element EXACTLY as-is, including the
                full onSave rebuild handler (markWorkProductsNeedingRebuild wiring). */}
            {refreshWarning && (
                <Alert severity="warning" onClose={() => setRefreshWarning(null)} sx={{ mb: 1.5 }}>
                    {refreshWarning}
                </Alert>
            )}

            <FileScopeModal
                open={showEditScopeModal}
                file={editingFile}
                currentAssignments={editingFile ? fileAssignments[editingFile.fileid] : null}
                onRefreshDelayed={setRefreshWarning}
                onClose={() => {
                    setShowEditScopeModal(false);
                    setEditingFile(null);
                }}
                onSave={async (savedScope: SavedScopeData) => {
                    fetchFileAssignments();
                    const tokenId = await getToken();
                    console.log("File scope saved:", savedScope);
                    console.log("Project IDs:", savedScope.project_ids);
                    console.log("Feature IDs:", savedScope.feature_ids);
                    console.log("Changes:", savedScope.changes);
                    console.log("Added Project IDs:", savedScope.changes.added_project_ids);
                    console.log("Removed Project IDs:", savedScope.changes.removed_project_ids);
                    console.log("Added Feature IDs:", savedScope.changes.added_feature_ids);
                    console.log("Removed Feature IDs:", savedScope.changes.removed_feature_ids);

                    // Only take action if there were actual changes
                    const hasChanges =
                        savedScope.changes.added_project_ids.length > 0 ||
                        savedScope.changes.removed_project_ids.length > 0 ||
                        savedScope.changes.added_feature_ids.length > 0 ||
                        savedScope.changes.removed_feature_ids.length > 0;

                    if (hasChanges && tokenId != null) {
                        // Collect all affected project and feature IDs (both added and removed need rebuild)
                        const allProjectIds = [
                            ...savedScope.changes.added_project_ids,
                            ...savedScope.changes.removed_project_ids
                        ];
                        const allFeatureIds = [
                            ...savedScope.changes.added_feature_ids,
                            ...savedScope.changes.removed_feature_ids
                        ];

                        console.log("Triggering rebuild for changed projects:", allProjectIds, "and features:", allFeatureIds);

                        // Make a single call with all affected IDs
                        markWorkProductsNeedingRebuild(
                            allProjectIds.length > 0 ? allProjectIds : null,
                            allFeatureIds.length > 0 ? allFeatureIds : null,
                            null, // user_story_ids
                            null, // source_warehouseid
                            true, // file_update
                            tokenId
                        ).catch(err => {
                            console.error("Error triggering rebuild for scope changes:", err);
                        });
                    }
                }}
            />
        </>
    );
});

FileViewer.displayName = "FileViewer";

export default FileViewer;
